import type {
    CreateReturnPayload,
    ReturnableProductListResponse,
    ReturnListResponse,
    ReturnRequest,
    ReturnStatus,
} from './types';

import { baseRequest } from '@/lib/base';
import { extractApiError } from '@/lib/shared-utils';

interface FetchReturnableProductsParams {
    partyId: number;
    page: number;
    pageSize: number;
    search?: string;
    returnableOnly?: boolean;
}

export const fetchReturnableProducts = async ({
    partyId,
    page,
    pageSize,
    search,
    returnableOnly,
}: FetchReturnableProductsParams): Promise<ReturnableProductListResponse> => {
    const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
    });

    if (search) params.append('search', search);
    if (returnableOnly) params.append('returnable_only', 'true');

    const response = await baseRequest({
        url: `/order/returns/parties/${partyId}/products/?${params.toString()}`,
    });

    if (!response.ok) {
        throw new Error(
            extractApiError(response.data, 'Failed to fetch returnable products.'),
        );
    }

    return response.data;
};

export const createReturnRequest = (
    partyId: number,
    payload: CreateReturnPayload,
) => baseRequest({
    url: `/order/returns/parties/${partyId}/`,
    method: 'POST',
    body: payload,
});

interface FetchReturnsParams {
    role: 'customer' | 'supplier';
    status?: ReturnStatus;
    search?: string;
    page: number;
    pageSize: number;
}

export const fetchReturns = async ({
    role,
    status,
    search,
    page,
    pageSize,
}: FetchReturnsParams): Promise<ReturnListResponse> => {
    const params = new URLSearchParams({
        role,
        page: String(page),
        page_size: String(pageSize),
    });

    if (status) params.append('status', status);
    if (search) params.append('search', search);

    const response = await baseRequest({
        url: `/order/returns/?${params.toString()}`,
    });

    if (!response.ok) {
        throw new Error(
            extractApiError(response.data, 'Failed to fetch returns.'),
        );
    }

    return response.data;
};

export const fetchReturnDetail = async (uuid: string): Promise<ReturnRequest> => {
    const response = await baseRequest({
        url: `/order/returns/${uuid}/`,
    });

    if (!response.ok) {
        throw new Error(
            extractApiError(response.data, 'Failed to fetch return details.'),
        );
    }

    return response.data;
};

export const approveReturn = (uuid: string) => baseRequest({
    url: `/order/returns/${uuid}/approve/`,
    method: 'POST',
});

export const rejectReturn = (uuid: string, reason: string) => baseRequest({
    url: `/order/returns/${uuid}/reject/`,
    method: 'POST',
    body: { reason },
});

// Customer-only, and only while the return is still pending. Hard-deletes
// the return — a 204 with no body.
export const deleteReturn = (uuid: string) => baseRequest({
    url: `/order/returns/${uuid}/`,
    method: 'DELETE',
});
